import { Link } from 'react-router-dom'
import { getEnabledProducts } from '../data/products'
import { isPageSeoEnabled } from '../config/siteConfig'
import SeoHead, { generateOrganizationSchema } from '../components/SeoHead'
import './Home.css'

const Home = () => {
  // Featured products (first 6 enabled products)
  const featuredProducts = getEnabledProducts().slice(0, 6)
  
  // Check if SEO is enabled for this page
  const seoEnabled = isPageSeoEnabled('home')
  
  // Organization schema for homepage
  const organizationSchema = generateOrganizationSchema()

  const growthDrivers = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
        </svg>
      ),
      title: 'Research & Development',
      description: 'Continuous innovation in pharmaceutical formulations for better patient outcomes.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
      ),
      title: 'Quality Assurance',
      description: 'GMP certified facilities with rigorous quality control at every stage.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
        </svg>
      ),
      title: 'Market Expansion',
      description: 'Expanding our reach to serve healthcare needs across India and beyond.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
      ),
      title: 'Expert Team',
      description: 'Experienced professionals dedicated to pharmaceutical excellence.'
    }
  ]

  const certifications = [
    { name: 'GMP Certified', description: 'Good Manufacturing Practice' },
    { name: 'ISO 9001:2015', description: 'Quality Management System' },
    { name: 'WHO-GMP', description: 'World Health Organization Standards' },
    { name: 'FSSAI', description: 'Food Safety Standards' }
  ]

  return (
    <div className="home">
      <SeoHead
        title="SIBR Lifescience | Quality Pharmaceutical Products | GMP Certified"
        description="SIBR Lifescience is a GMP-certified pharmaceutical company manufacturing high-quality medicines. Explore our range of healthcare products."
        canonical="/"
        schema={organizationSchema}
        seoEnabled={seoEnabled}
      />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="container">
          <div className="hero-content">
            <span className="section-subtitle animate-fade-in">Trusted Pharmaceutical Partner</span>
            <h1 className="animate-fade-in-up">
              Quality Healthcare<br />
              <span className="text-highlight">For Better Lives</span>
            </h1>
            <p className="hero-description animate-fade-in-up">
              SIBR Lifescience is committed to developing and manufacturing high-quality 
              pharmaceutical products that improve patient outcomes. Our GMP-certified 
              facilities ensure every product meets the highest standards.
            </p>
            <div className="hero-actions animate-fade-in-up">
              <Link to="/products" className="btn btn-primary btn-lg">
                Explore Products
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </Link>
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-lg"
              >
                Find on 1mg
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission section">
        <div className="container">
          <div className="mission-content">
            <span className="section-subtitle">Our Mission</span>
            <h2>Advancing Healthcare Through Quality</h2>
            <p className="mission-text">
              At SIBR Lifescience, we believe that everyone deserves access to safe, effective, 
              and affordable healthcare products. Our mission is to develop pharmaceutical 
              formulations that meet international quality standards while remaining accessible 
              to patients across India.
            </p>
            <div className="mission-stats">
              <div className="stat">
                <span className="stat-number">10+</span>
                <span className="stat-label">Products</span>
              </div>
              <div className="stat">
                <span className="stat-number">1000+</span>
                <span className="stat-label">Retail Partners</span>
              </div>
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">Quality Compliance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section section bg-light">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Our Products</span>
            <h2>Product Portfolio</h2>
            <p>
              Explore our range of pharmaceutical products designed to address 
              various healthcare needs with proven efficacy and safety.
            </p>
          </div>

          <div className="products-grid">
            {featuredProducts.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={`${product.name} - SIBR Lifescience pharmaceutical medicine`}
                      className="product-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="product-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                    </svg>
                  </div>
                </div>
                <div className="product-content">
                  <h3>{product.name}</h3>
                  <p>{product.shortDescription}</p>
                  <Link to={`/products/${product.id}`} className="product-link">
                    View Details
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="products-cta">
            <Link to="/products" className="btn btn-primary">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Growth Drivers Section */}
      <section className="growth-section section">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Why Choose Us</span>
            <h2>Our Growth Drivers</h2>
            <p>
              The pillars that drive our commitment to pharmaceutical excellence.
            </p>
          </div>

          <div className="growth-grid">
            {growthDrivers.map((driver, index) => (
              <div key={index} className="growth-card">
                <div className="growth-icon">{driver.icon}</div>
                <h3>{driver.title}</h3>
                <p>{driver.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Endeavors / Roadmap */}
      <section className="roadmap-section section bg-light">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Our Vision</span>
            <h2>Future Endeavors</h2>
            <p>Our roadmap for continued growth and innovation in healthcare.</p>
          </div>

          <div className="roadmap">
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <span>1</span>
              </div>
              <div className="roadmap-content">
                <h4>Product Line Expansion</h4>
                <p>Developing new formulations to address emerging healthcare needs.</p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <span>2</span>
              </div>
              <div className="roadmap-content">
                <h4>Market Reach</h4>
                <p>Expanding distribution network to serve more patients across India.</p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <span>3</span>
              </div>
              <div className="roadmap-content">
                <h4>International Standards</h4>
                <p>Achieving additional international certifications and export readiness.</p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <span>4</span>
              </div>
              <div className="roadmap-content">
                <h4>Research Partnerships</h4>
                <p>Collaborating with research institutions for advanced formulations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Certification Banner */}
      <section className="trust-section section">
        <div className="container">
          <div className="trust-content">
            <h2>Committed to Quality & Compliance</h2>
            <p>
              Our manufacturing facilities adhere to stringent quality standards, 
              ensuring every product meets regulatory requirements.
            </p>
            <div className="certification-grid">
              {certifications.map((cert, index) => (
                <div key={index} className="certification-card">
                  <div className="cert-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                    </svg>
                  </div>
                  <h4>{cert.name}</h4>
                  <p>{cert.description}</p>
                </div>
              ))}
            </div>
            <Link to="/quality" className="btn btn-white">
              Learn About Our Quality Standards
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-sm bg-light">
        <div className="container">
          <div className="cta-content">
            <h2>Looking for Quality Pharmaceutical Products?</h2>
            <p>Find our products on leading healthcare platforms.</p>
            <div className="cta-buttons">
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-lg"
              >
                Find Products on 1mg
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
